import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FolderOpen,
  FileText,
  Image as ImageIcon,
  Video,
  Eye,
  Download,
  Pencil,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { FilterChip } from '@/components/ui/Chip';
import { MenuItem } from '@/components/ui/Popover';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { ColumnSortHeader } from '@/components/ui/ColumnSortHeader';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { documentAccessFailureKey } from '@/lib/documentStorageAccess';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { PatientTabIconButton, PatientTabMoreMenu } from '@/components/patient/PatientTabActions';
import {
  PatientTabFrame,
  PatientTabSection,
  PatientTabToolbar,
} from '@/components/patient/PatientTabFrame';
import {
  usePatientDocuments,
  useGetDocumentDownloadUrl,
  useUploadClinicPatientDocument,
  useUpdatePatientDocument,
  useDeletePatientDocument,
  type DocumentRow,
} from '@/hooks/useDocuments';
import {
  usePatientFileCategories,
  useSetDocumentsCategory,
  type PatientFileCategoryRow,
} from '@/hooks/usePatientFileCategories';
import {
  FilePreviewOverlay,
  type FilePreviewSource,
} from '@/components/documents/FilePreviewOverlay';
import { FileCategoryChip } from '@/components/documents/FileCategoryChip';
import { FileCategoryPicker } from '@/components/documents/FileCategoryPicker';
import { UploadFileSlideOver } from '@/components/documents/UploadFileSlideOver';
import { EditPatientFileDialog } from '@/components/documents/EditPatientFileDialog';
import { DeletePatientFileDialog } from '@/components/documents/DeletePatientFileDialog';
import { documentListMeta } from '@/lib/documentSource';
import { explicitPatientFileDocumentDate, patientFileListName } from '@/lib/patientFileLabel';
import { downloadUrlAsFile } from '@/utils/documentDownload';
import {
  ALL_FILE_CATEGORIES_ID,
  UNCATEGORIZED_FILE_CATEGORY_ID,
  assignableFileCategories,
  fileCategoryFilterCounts,
  filterPatientFilesByCategory,
  groupPatientFilesByCategory,
  resolveFileCategory,
} from '@/lib/patientFileCategories';
import {
  nextPatientFileSort,
  readPatientFileSort,
  sortPatientFiles,
  writePatientFileSort,
  type PatientFileSort,
  type PatientFileSortKey,
} from '@/lib/patientFileSort';

interface PatientDocumentsSectionProps {
  clinicId: string;
  patientId: string;
  userId?: string | null;
  isAdmin?: boolean;
  canUpload?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

function fileIcon(mimeType: string) {
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

function FileDocumentDate({ doc, testId }: { doc: DocumentRow; testId: string }) {
  return (
    <span className={cn(TYPE.small, 'text-ds-muted')} data-testid={testId}>
      {explicitPatientFileDocumentDate(doc) ?? ''}
    </span>
  );
}

function fileAriaSort(
  sort: PatientFileSort,
  key: PatientFileSortKey,
): 'ascending' | 'descending' | 'none' {
  if (sort.key !== key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

export function PatientDocumentsSection({
  clinicId,
  patientId,
  userId = null,
  isAdmin: _isAdmin = false,
  canUpload = false,
  canUpdate = false,
  canDelete = false,
}: PatientDocumentsSectionProps) {
  const { t } = useTranslation();
  const docs = usePatientDocuments(clinicId, patientId);
  const categoriesQuery = usePatientFileCategories(clinicId);
  const getDownloadUrl = useGetDocumentDownloadUrl();
  const uploadClinicFile = useUploadClinicPatientDocument();
  const updatePatientFile = useUpdatePatientDocument();
  const deletePatientFile = useDeletePatientDocument();
  const setCategory = useSetDocumentsCategory();
  const [preview, setPreview] = useState<FilePreviewSource | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filter, setFilter] = useState(ALL_FILE_CATEGORIES_ID);
  const [grouped, setGrouped] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [sort, setSort] = useState<PatientFileSort>(() => readPatientFileSort(userId));
  const [editing, setEditing] = useState<DocumentRow | null>(null);
  const [deleting, setDeleting] = useState<DocumentRow | null>(null);
  const [pendingUploads, setPendingUploads] = useState<{ id: string; name: string }[]>([]);

  const categories = categoriesQuery.data ?? [];
  const rows = docs.data ?? [];
  const counts = fileCategoryFilterCounts(rows, categories);
  const visible = useMemo(
    () => sortPatientFiles(filterPatientFilesByCategory(rows, filter), sort),
    [rows, filter, sort],
  );
  const empty = !docs.isLoading && !docs.isError && rows.length === 0 && pendingUploads.length === 0;
  const assignable = assignableFileCategories(categories);
  const selectedVisible = selected.filter((id) => visible.some((row) => row.id === id));
  const chipCategories = categories.filter(
    (category) => !category.is_archived || (counts[category.id] ?? 0) > 0,
  );

  if (!clinicId) return null;

  const applySort = (key: PatientFileSortKey) => {
    const next = nextPatientFileSort(sort, key);
    setSort(next);
    writePatientFileSort(userId, next);
  };

  const sortHeader = (key: PatientFileSortKey, label: string) => (
    <ColumnSortHeader
      label={label}
      active={sort.key === key}
      direction={sort.dir}
      onClick={() => applySort(key)}
      ariaLabel={
        sort.key === key
          ? t(sort.dir === 'asc' ? 'patients.files.sorted_asc' : 'patients.files.sorted_desc', {
              column: label,
            })
          : t('patients.files.sort_by', { column: label })
      }
    />
  );

  const fileSortColumns: { id: PatientFileSortKey; label: string }[] = [
    { id: 'name', label: t('patients.files.col_name') },
    { id: 'documentDate', label: t('patients.files.col_document_date') },
    { id: 'date', label: t('patients.files.col_date') },
  ];

  const handleView = (doc: DocumentRow) => {
    setPreview({
      type: 'document',
      documentId: doc.id,
      filename: doc.original_filename,
      mimeType: doc.mime_type,
    });
  };

  const handleDownload = async (doc: DocumentRow) => {
    try {
      const url = await getDownloadUrl.mutateAsync({
        documentId: doc.id,
        filename: doc.original_filename,
      });
      await downloadUrlAsFile({
        url,
        mimeType: doc.mime_type,
        filename: doc.original_filename,
      });
    } catch (error) {
      toast.error(t(documentAccessFailureKey(error)));
    }
  };

  const handleUpload = async (files: File[], categoryId: string | null, documentDate: string) => {
    const pending = files.map((file) => ({ id: crypto.randomUUID(), name: file.name }));
    setPendingUploads((current) => [...pending, ...current]);
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const pendingId = pending[index]?.id;
      if (!file || !pendingId) continue;
      try {
        await uploadClinicFile.mutateAsync({
          clinicId,
          patientId,
          file,
          categoryId,
          documentDate,
        });
        toast.success(t('documents.upload_success'));
      } catch {
        toast.error(t('documents.upload_failed'));
      } finally {
        setPendingUploads((current) => current.filter((row) => row.id !== pendingId));
      }
    }
  };

  const applyCategory = async (documentIds: string[], categoryId: string | null) => {
    if (documentIds.length === 0) return;
    try {
      await setCategory.mutateAsync({ documentIds, categoryId });
      toast.success(t('patients.files.category_updated'));
      setSelected([]);
    } catch {
      toast.error(t('patients.files.category_update_failed'));
    }
  };

  const openEdit = (doc: DocumentRow) => {
    setEditing(doc);
  };

  const handleSaveEdit = async (input: {
    originalFilename: string;
    documentDate: string;
    categoryId: string | null;
  }) => {
    if (!editing) return;
    try {
      await updatePatientFile.mutateAsync({
        documentId: editing.id,
        originalFilename: input.originalFilename,
        documentDate: input.documentDate,
      });
      if (input.categoryId !== editing.category_id) {
        await setCategory.mutateAsync({
          documentIds: [editing.id],
          categoryId: input.categoryId,
        });
      }
      toast.success(t('patients.files.edit_saved'));
      setEditing(null);
    } catch {
      toast.error(t('patients.files.edit_failed'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    try {
      await deletePatientFile.mutateAsync(deleting.id);
      toast.success(t('patients.files.delete_saved'));
      setDeleting(null);
      setSelected((current) => current.filter((id) => id !== deleting.id));
    } catch {
      toast.error(t('patients.files.delete_failed'));
    }
  };

  const toggleSelected = (id: string, checked: boolean) => {
    setSelected((current) =>
      checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id),
    );
  };

  const toggleAllVisible = (tableRows: DocumentRow[], checked: boolean) => {
    const ids = tableRows.map((row) => row.id);
    setSelected((current) =>
      checked
        ? Array.from(new Set([...current, ...ids]))
        : current.filter((id) => !ids.includes(id)),
    );
  };

  const categoryLabel = (doc: DocumentRow) => {
    const category = resolveFileCategory(categories, doc.category_id);
    return category?.name ?? t('patients.files.uncategorized');
  };

  const categoryToken = (doc: DocumentRow) => {
    return resolveFileCategory(categories, doc.category_id)?.color_token ?? 'muted';
  };

  const renderFileTable = (tableRows: DocumentRow[]) => {
    const columns: TableColumn<DocumentRow>[] = [
      ...(canUpdate
        ? [
            {
              id: 'select',
              header: (
                <Checkbox
                  aria-label={t('patients.files.select_all')}
                  checked={
                    tableRows.length > 0 && tableRows.every((row) => selected.includes(row.id))
                  }
                  onChange={(event) => toggleAllVisible(tableRows, event.target.checked)}
                />
              ),
              cell: (doc: DocumentRow) => (
                <Checkbox
                  aria-label={t('patients.files.select_file', { name: patientFileListName(doc) })}
                  checked={selected.includes(doc.id)}
                  onChange={(event) => toggleSelected(doc.id, event.target.checked)}
                />
              ),
              hideOnMobile: true,
            } satisfies TableColumn<DocumentRow>,
          ]
        : []),
        {
          id: 'name',
          header: sortHeader('name', t('patients.files.col_name')),
          sortDirection: fileAriaSort(sort, 'name'),
          cell: (doc: DocumentRow) => {
            const Icon = fileIcon(doc.mime_type);
            return (
              <button
                type="button"
                onClick={() => handleView(doc)}
                className="inline-flex min-w-0 items-center gap-s2 text-left"
              >
                <Icon className="h-4 w-4 shrink-0 text-ds-muted" />
                <span className="min-w-0">
                  <span
                    className={cn(TYPE.body, 'block truncate text-ds-text')}
                    data-testid={`patient-file-name-${doc.id}`}
                  >
                    {patientFileListName(doc)}
                  </span>
                  <span className="mt-s1 inline-flex nav:hidden">
                    <FileCategoryChip name={categoryLabel(doc)} colorToken={categoryToken(doc)} />
                  </span>
                </span>
              </button>
            );
          },
        },
        {
          id: 'category',
          header: t('patients.files.col_category'),
          hideOnMobile: true,
          cell: (doc: DocumentRow) =>
            canUpdate ? (
              <FileCategoryPicker
                categories={categories}
                value={doc.category_id}
                onChange={(categoryId) => void applyCategory([doc.id], categoryId)}
                disabled={setCategory.isPending}
              />
            ) : (
              <FileCategoryChip name={categoryLabel(doc)} colorToken={categoryToken(doc)} />
            ),
        },
        {
          id: 'document_date',
          header: sortHeader('documentDate', t('patients.files.col_document_date')),
          sortDirection: fileAriaSort(sort, 'documentDate'),
          cell: (doc: DocumentRow) => (
            <FileDocumentDate doc={doc} testId={`patient-file-document-date-${doc.id}`} />
          ),
        },
        {
          id: 'meta',
          header: sortHeader('date', t('patients.files.col_date')),
          sortDirection: fileAriaSort(sort, 'date'),
          cell: (doc: DocumentRow) => (
            <span className={cn(TYPE.small, 'text-ds-muted')} data-testid={`patient-file-meta-${doc.id}`}>
              {documentListMeta({
                mimeType: doc.mime_type,
                sizeBytes: doc.size_bytes,
                createdAt: doc.created_at,
                documentDate: doc.document_date,
                tags: doc.tags,
                t,
              })}
            </span>
          ),
        },
        {
          id: 'actions',
          header: t('patients.files.col_actions'),
          cell: (doc: DocumentRow) => (
            <div className="flex items-center gap-s1">
              <PatientTabIconButton
                label={t('documents.view')}
                onClick={() => handleView(doc)}
              >
                <Eye className="h-4 w-4" />
              </PatientTabIconButton>
              <PatientTabIconButton
                label={t('documents.download')}
                onClick={() => void handleDownload(doc)}
                disabled={getDownloadUrl.isPending}
              >
                <Download className="h-4 w-4" />
              </PatientTabIconButton>
              {canUpdate ? (
                <PatientTabIconButton
                  label={t('common.edit')}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openEdit(doc);
                  }}
                  data-testid={`patient-file-edit-${doc.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </PatientTabIconButton>
              ) : null}
              {canDelete ? (
                <PatientTabIconButton
                  label={t('patients.files.delete')}
                  onClick={() => setDeleting(doc)}
                  data-testid={`patient-file-delete-${doc.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </PatientTabIconButton>
              ) : null}
              {canUpdate ? (
                <PatientTabMoreMenu>
                  <MenuItem onClick={() => void applyCategory([doc.id], null)}>
                    {t('patients.files.uncategorized')}
                  </MenuItem>
                  {assignable.map((category) => (
                    <MenuItem
                      key={category.id}
                      active={doc.category_id === category.id}
                      onClick={() => void applyCategory([doc.id], category.id)}
                    >
                      {category.name}
                    </MenuItem>
                  ))}
                </PatientTabMoreMenu>
              ) : null}
            </div>
          ),
        },
    ];

    return (
    <ResponsiveTable
      rows={tableRows}
      getRowKey={(row) => row.id}
      columns={columns}
      mobileCard={(doc) => (
        <FileMobileCard
          doc={doc}
          categories={categories}
          canUpdate={canUpdate}
          canDelete={canDelete}
          selected={selected.includes(doc.id)}
          onSelect={(checked) => toggleSelected(doc.id, checked)}
          onView={() => handleView(doc)}
          onDownload={() => void handleDownload(doc)}
          onEdit={() => openEdit(doc)}
          onDelete={() => setDeleting(doc)}
          onCategory={(categoryId) => void applyCategory([doc.id], categoryId)}
          downloadPending={getDownloadUrl.isPending}
          categoryPending={setCategory.isPending}
        />
      )}
    />
    );
  };

  const groups = grouped ? groupPatientFilesByCategory(visible, categories) : null;

  return (
    <>
      {preview && <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} />}
      {canUpload ? (
        <UploadFileSlideOver
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onUpload={handleUpload}
          uploading={uploadClinicFile.isPending}
          categories={categories}
        />
      ) : null}
      {editing ? (
        <EditPatientFileDialog
          doc={editing}
          categories={categories}
          pending={updatePatientFile.isPending || setCategory.isPending}
          onClose={() => setEditing(null)}
          onSave={handleSaveEdit}
        />
      ) : null}
      {deleting ? (
        <DeletePatientFileDialog
          doc={deleting}
          pending={deletePatientFile.isPending}
          onClose={() => setDeleting(null)}
          onConfirm={() => void handleConfirmDelete()}
        />
      ) : null}

    <PatientTabFrame
      title={t('patients.tabs.documents')}
      count={rows.length}
      action={
        canUpload ? (
            <Button
              type="button"
              data-testid="patient-files-upload-button"
              onClick={() => setUploadOpen(true)}
            >
              {t('patients.files.upload')}
            </Button>
        ) : undefined
      }
      loading={docs.isLoading}
      error={docs.isError ? t('documents.load_error') : undefined}
      onRetry={() => void docs.refetch()}
      toolbar={
        empty ? undefined : (
          <PatientTabToolbar
            chips={
              <>
                <FilterChip
                  pressed={filter === ALL_FILE_CATEGORIES_ID}
                  count={counts[ALL_FILE_CATEGORIES_ID]}
                  onClick={() => setFilter(ALL_FILE_CATEGORIES_ID)}
                >
                  {t('patients.files.filter_all')}
                </FilterChip>
                {chipCategories.map((category) => (
                  <FilterChip
                    key={category.id}
                    pressed={filter === category.id}
                    count={counts[category.id] ?? 0}
                    onClick={() => setFilter(category.id)}
                  >
                    {category.name}
                  </FilterChip>
                ))}
                {(counts[UNCATEGORIZED_FILE_CATEGORY_ID] ?? 0) > 0 ? (
                  <FilterChip
                    pressed={filter === UNCATEGORIZED_FILE_CATEGORY_ID}
                    count={counts[UNCATEGORIZED_FILE_CATEGORY_ID]}
                    onClick={() => setFilter(UNCATEGORIZED_FILE_CATEGORY_ID)}
                  >
                    {t('patients.files.uncategorized')}
                  </FilterChip>
                ) : null}
              </>
            }
            filters={
              <div className="flex flex-col gap-s2 nav:flex-row nav:items-end">
                <SegmentedControl
                  ariaLabel={t('patients.files.group')}
                  value={grouped ? 'grouped' : 'list'}
                  onChange={(value) => setGrouped(value === 'grouped')}
                  options={[
                    { value: 'list', label: t('patients.files.group_list') },
                    { value: 'grouped', label: t('patients.files.group_by_category') },
                  ]}
                />
              </div>
            }
          />
        )
      }
      empty={
        empty
          ? {
              icon: <FolderOpen className="h-7 w-7" />,
              title: t('patients.files.empty'),
              description: t('patients.files.empty_hint'),
            }
          : undefined
      }
    >
        {canUpdate && selectedVisible.length > 0 ? (
          <div className="mt-s4 flex flex-col gap-s3 rounded-pop bg-ds-sunk px-s3 py-s3 nav:flex-row nav:items-end">
            <p className={cn(TYPE.small, 'text-ds-muted')}>
              {t('patients.files.selected', { count: selectedVisible.length })}
            </p>
            <div className="min-w-0 flex-1 nav:max-w-xs">
              <FileCategoryPicker
                id="bulk-file-category"
                label={t('patients.files.bulk_change')}
                placeholder={t('patients.files.change_category')}
                categories={categories}
                value={null}
                onChange={(categoryId) => void applyCategory(selectedVisible, categoryId)}
                disabled={setCategory.isPending}
              />
            </div>
          </div>
        ) : null}
        <div className="mt-s4">
          <div
            className={cn(
              'mb-s3 flex flex-wrap gap-x-s4 gap-y-s2 border-b border-ds-line px-s3 py-s3',
              !(grouped && visible.length === 0) && 'nav:hidden',
            )}
          >
            {fileSortColumns.map((column) => (
              <span key={column.id}>{sortHeader(column.id, column.label)}</span>
            ))}
          </div>
          {pendingUploads.length > 0 ? (
            <ul className="mb-s3 flex flex-col gap-s2" aria-live="polite">
              {pendingUploads.map((item) => (
                <li
                  key={item.id}
                  className={cn(TYPE.small, 'text-ds-muted')}
                  data-testid={`patient-file-pending-${item.id}`}
                >
                  {item.name} · {t('patients.files.uploading')}
                </li>
              ))}
            </ul>
          ) : null}
          {groups
            ? groups.map((group) => (
                <PatientTabSection
                  key={group.key}
                  title={group.category?.name ?? t('patients.files.uncategorized')}
                  meta={
                    <FileCategoryChip
                      name={String(group.files.length)}
                      colorToken={group.category?.color_token ?? 'muted'}
                    />
                  }
                >
                  {renderFileTable(group.files)}
                </PatientTabSection>
              ))
            : renderFileTable(visible)}
        </div>
    </PatientTabFrame>
    </>
  );
}

function FileMobileCard({
  doc,
  categories,
  canUpdate,
  canDelete,
  selected,
  onSelect,
  onView,
  onDownload,
  onEdit,
  onDelete,
  onCategory,
  downloadPending,
  categoryPending,
}: {
  doc: DocumentRow;
  categories: readonly PatientFileCategoryRow[];
  canUpdate: boolean;
  canDelete: boolean;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onView: () => void;
  onDownload: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCategory: (categoryId: string | null) => void;
  downloadPending: boolean;
  categoryPending: boolean;
}) {
  const { t } = useTranslation();
  const Icon = fileIcon(doc.mime_type);
  const category = resolveFileCategory(categories, doc.category_id);

  return (
    <div className="flex flex-col gap-s3">
      <div className="flex items-start gap-s3">
        {canUpdate ? (
          <Checkbox
            aria-label={t('patients.files.select_file', { name: patientFileListName(doc) })}
            checked={selected}
            onChange={(event) => onSelect(event.target.checked)}
          />
        ) : null}
        <button type="button" onClick={onView} className="min-w-0 flex-1 text-left">
          <span className="inline-flex min-w-0 items-center gap-s2">
            <Icon className="h-4 w-4 shrink-0 text-ds-muted" />
            <span
              className={cn(TYPE.body, 'truncate text-ds-text')}
              data-testid={`patient-file-name-${doc.id}`}
            >
              {patientFileListName(doc)}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-s1">
          <PatientTabIconButton label={t('documents.view')} onClick={onView}>
            <Eye className="h-4 w-4" />
          </PatientTabIconButton>
          <PatientTabIconButton
            label={t('documents.download')}
            onClick={onDownload}
            disabled={downloadPending}
          >
            <Download className="h-4 w-4" />
          </PatientTabIconButton>
          {canUpdate ? (
            <PatientTabIconButton
              label={t('common.edit')}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onEdit();
              }}
              data-testid={`patient-file-edit-${doc.id}`}
            >
              <Pencil className="h-4 w-4" />
            </PatientTabIconButton>
          ) : null}
          {canDelete ? (
            <PatientTabIconButton
              label={t('patients.files.delete')}
              onClick={onDelete}
              data-testid={`patient-file-delete-${doc.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </PatientTabIconButton>
          ) : null}
        </div>
      </div>
      <FileCategoryChip
        name={category?.name ?? t('patients.files.uncategorized')}
        colorToken={category?.color_token ?? 'muted'}
      />
      <FileDocumentDate doc={doc} testId={`patient-file-document-date-${doc.id}`} />
      {canUpdate ? (
        <FileCategoryPicker
          categories={categories}
          value={doc.category_id}
          onChange={onCategory}
          disabled={categoryPending}
        />
      ) : null}
      <p className={cn(TYPE.small, 'text-ds-muted')} data-testid={`patient-file-meta-${doc.id}`}>
        {documentListMeta({
          mimeType: doc.mime_type,
          sizeBytes: doc.size_bytes,
          createdAt: doc.created_at,
          documentDate: doc.document_date,
          tags: doc.tags,
          t,
        })}
      </p>
    </div>
  );
}
